import { HTTPMethod } from "@fusionauth/typescript-client";
import { Button, Stack } from "@mui/material";
import {
  type GridColDef,
  GridRow,
  type GridRowClassNameParams,
  type GridRowProps,
  type RowPropsOverrides,
} from "@mui/x-data-grid-premium";
import React, {
  type ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { ProjectContext } from "../contexts/ProjectContext.tsx";
import { projectConfigurationError } from "../models/Project.ts";
import { useFetch } from "../services/hooks/useFetch.ts";
import { apiPrefix } from "../utils/main.ts";
import CloseArrowTooltip from "./CloseArrowTooltip.tsx";
import AccordionTable from "./datagrid/AccordionTable.tsx";

const TooltipRow = (props: GridRowProps & RowPropsOverrides) => {
  if (!props.row.errors || props.row.errors === "")
    return <GridRow {...props} />;
  const errors = props.row.errors.replaceAll(";", "\n");
  return (
    <CloseArrowTooltip
      title={<div style={{ whiteSpace: "pre-line" }}>{errors}</div>}
    >
      <GridRow {...props} />
    </CloseArrowTooltip>
  );
};

export default function CohortUpload({
  editable,
  cohortListInitiallyExpanded,
  cohortList,
  onUpdateData,
  setCohortValidated,
}: {
  editable: boolean;
  cohortListInitiallyExpanded: boolean;
  cohortList: CohortData[];
  onUpdateData: (data: CohortData[]) => void;
  setCohortValidated: (data: boolean) => void;
}) {
  const project: ProjectContextType | null = useContext(ProjectContext);
  const projectId = project?.id;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const title = "Cohort";

  const { create: validateCohort, error: validateCohortError } = useFetch<
    undefined,
    CohortData[],
    CohortData[]
  >();

  const fields: GridColDef<CohortData>[] = [
    {
      field: "patientNumber",
      headerName: "#",
      width: 70,
      filterable: false,
    },
    {
      field: "patientId",
      headerName: "Patient ID",
      width: 250,
      filterable: true,
    },
    {
      field: "indexEncounter",
      headerName: "Index Encounter",
      width: 250,
      filterable: false,
    },
    {
      field: "indexDate",
      headerName: "Index Date",
      width: 110,
      filterable: false,
    },
  ];

  const getRowClassName = useCallback(
    (params: GridRowClassNameParams<CohortData>) => {
      if (!!params.row.errors && params.row.errors.length > 0) {
        return "table-row-error";
      }

      if (params.row.isNew) {
        return "table-row-new";
      }

      return "";
    },
    [],
  );

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile: File | undefined = event.target.files
      ? event.target.files[0]
      : undefined;
    if (!selectedFile) {
      alert("Please select a file first!");
      return;
    }

    setSelectedFile(selectedFile);

    const uploadFormData = new FormData();
    uploadFormData.append("cohortFile", selectedFile, selectedFile.name);
    uploadFormData.append("projectType", `${project?.type}`);

    fetch(`${apiPrefix}/projects/${projectId}/cohort-upload`, {
      method: "POST",
      body: uploadFormData,
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        onUpdateData(data);
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  const handleCohortValidate = function () {
    void validateCohort({
      path: `${apiPrefix}/projects/${projectId}/cohort-validate`,
      body: cohortList,
      method: HTTPMethod.POST,
      responseManipulationFunction: (data: CohortData[]) => {
        const hasCohortListErrors = data?.some((patient) => patient.errors);
        setCohortValidated(!hasCohortListErrors);
        onUpdateData(data);
      },
    });
  };

  useEffect(() => {
    if (
      validateCohortError &&
      typeof validateCohortError === "object" &&
      "error" in validateCohortError
    ) {
      switch (validateCohortError.error) {
        case projectConfigurationError.cohortInvalidPatientInDataSource:
          // Set patient errors
          if (
            "invalidCohortData" in validateCohortError &&
            Array.isArray(validateCohortError.invalidCohortData)
          ) {
            for (const invalidRow of validateCohortError.invalidCohortData) {
              const existingRow = cohortList!.find(
                (row) => row.patientId === invalidRow.patientId,
              );
              if (
                existingRow &&
                "message" in validateCohortError &&
                typeof validateCohortError.message === "string"
              ) {
                existingRow.errors = validateCohortError.message;
              }
            }
          }

          // Update the cohort list with our new errors
          onUpdateData(cohortList);

          document.getElementById(`${title}-header`)?.scrollIntoView();
          break;
        default:
          break;
      }
    }
  }, [validateCohortError, onUpdateData, cohortList]);

  useEffect(() => {
    if (!editable) {
      setCohortValidated(false);
      setSelectedFile(null);
    }
  }, [editable, setCohortValidated]);

  return (
    <section className="subsection">
      {editable && (
        <Stack>
          <input
            type="file"
            id="file-upload"
            accept=".csv,.txt"
            style={{ display: "none" }}
            onChange={handleFileUpload}
          />
          <label htmlFor="file-upload">
            <Button variant="contained" component="span">
              Select file to upload
            </Button>
            <Button
              variant="contained"
              sx={{ width: 175, marginLeft: 2 }}
              onClick={handleCohortValidate}
              disabled={!selectedFile}
            >
              Validate source data
            </Button>
          </label>
          {selectedFile !== null && <p>Selected file: {selectedFile.name}</p>}
          <br />
        </Stack>
      )}
      {cohortList && (
        <Stack>
          <AccordionTable<CohortData>
            startExpanded={cohortListInitiallyExpanded}
            title={title}
            totalCount={cohortList.length}
            tableId="cohort-table"
            loading={false}
            initialState={{
              sorting: {
                sortModel: [
                  { field: "patientNumber", sort: "asc" },
                  { field: "patientId", sort: "asc" },
                ],
              },
            }}
            slots={{
              row: TooltipRow,
            }}
            slotProps={{
              toolbar: {
                showExportCsv: !editable,
                showViewMenu: false,
                showFilter: false,
                showQuickFilter: true,
                quickFilterColumns: "patientId, indexEncounter, indexDate",
                csvOptions: {
                  fileName: `${project?.name} patient-cohort.csv`,
                },
              },
            }}
            showToolbar={!editable}
            getRowHeight={() => 45}
            getRowId={(cohortData) => cohortData.patientId}
            columns={fields}
            rows={cohortList}
            getRowClassName={getRowClassName}
          />
        </Stack>
      )}
    </section>
  );
}
